import { defineDocument, type LineGridDef } from './types.js'
import {
  ALL_TABS, BASIC_ACTIONS, FULL_ACTIONS, baseColumns, baseFilters, col, f, interstate, jobType, taxUnit, taxUnitChip,
  OURS, ofParty, ofPartyOrOurs, scoped,
} from './common.js'

/**
 * The BOQ, and the estimate underneath it.
 *
 * A BOQ line is what the customer ordered and at what price. An estimation
 * line is what we think it will cost us to deliver that BOQ line, broken into
 * material, labour, bundled work and lump sums. Everything the business wants
 * to know — am I making money on this job, what is left to buy, what may I
 * still bill — is a comparison between those two, which is why they are
 * modelled as two grids on one document rather than as free text.
 */

/*
  The reference's five, in its order. Material and Labour is one line priced
  twice — a material rate and a labour rate, each reaching its own pending
  report — where Bundled is one combined rate. Expense is kept so lines saved
  before Lumpsum existed still read; it counts as a lump sum.
*/
export const COST_TYPES = [
  { value: 'MATERIAL', label: 'Material' },
  { value: 'LABOUR', label: 'Labour' },
  { value: 'MATERIAL_AND_LABOUR', label: 'Material and Labour' },
  { value: 'BUNDLED', label: 'Bundled' },
  { value: 'LUMPSUM', label: 'Lumpsum' },
  { value: 'EXPENSE', label: 'Expense (as lumpsum)' },
] as const

/** Which items an estimate line may pick, by its cost type. */
const ITEM_BY_COST_TYPE = {
  column: 'costType',
  entities: {
    MATERIAL: 'material', MATERIAL_AND_LABOUR: 'material', LABOUR: 'labour',
    LUMPSUM: 'expense_head', EXPENSE: 'expense_head',
  },
} as const

/** A lump sum is an amount, not a quantity at a rate: one of it. */
const LUMPSUM_TYPES = ['LUMPSUM', 'EXPENSE'] as const

const YES_NO = [{ value: 'YES', label: 'Yes' }, { value: 'NO', label: 'No' }] as const

/** Shown on an Item line only; a Section/Description line is its refs and its words. */
const ITEM_ONLY = { column: 'lineType', equals: ['ITEM'] } as const

/** A Single Rate project prices one rate, read as "Rate" (PROJ2-024). */
const SINGLE_RATE = { field: 'pricingStyle', equals: ['SINGLE_RATE'] } as const

/**
 * docs/05 §1 §BOQ — section headings and priced items, in one tree.
 *
 * Reference "BOQ and Estimation": Internal Ref, Client Ref, Description,
 * Unit, Order Qty, Design Qty, Supply Rate, Installation Rate, Supply
 * Amount, Installation Amount, Total Amount — our working columns after
 * those, and the flags the grid has no room for in the BOQ Detail dialog
 * that "+ Add BOQ" opens. Each line's estimate is drawn under it.
 */
const boqGrid: LineGridDef = {
  section: 'BOQ',
  title: 'BOQ and Estimation', n: '05',
  addLabel: 'Add BOQ',
  addOpensDialog: true,
  detailTitle: 'BOQ Detail',
  rowDefaults: { lineType: 'ITEM' },
  columns: [
    col.seq(),
    // Asked first in BOQ Detail, as a radio pair; the grid shows it by what
    // the row draws (a Section/Description line carries no figures).
    { key: 'lineType', label: 'Line type', type: 'text', width: 150, editable: true, required: true,
      control: 'radio', dialogOnly: true,
      options: [
        { value: 'SECTION', label: 'Section/Description' },
        { value: 'ITEM', label: 'Item' },
      ] },
    { key: 'boqCode', label: 'Internal Ref', type: 'text', width: 112, editable: true, required: true,
      hint: 'Our reference for the line. Sub-items read 12.1, 86 - 1.a.' },
    { key: 'clientRef', label: 'Client Ref', type: 'text', width: 112, editable: true,
      hint: 'The reference the customer uses in their own BOQ, when it differs from ours.' },
    { ...col.desc(), secondary: false, required: true, clamp: 3, width: 320 },
    // The material the line is, when it is one; the description is what the
    // customer's BOQ says (reference: a free Description*).
    { ...col.item('Item (optional)'), dialogOnly: true, visibleWhen: ITEM_ONLY },
    { ...col.unit(), visibleWhen: ITEM_ONLY },
    { key: 'quantity', label: 'Order qty', type: 'qty', width: 108, align: 'right', editable: true,
      visibleWhen: ITEM_ONLY },
    { key: 'designQuantity', label: 'Design qty', type: 'qty', width: 108, align: 'right', editable: true,
      visibleWhen: ITEM_ONLY,
      hint: 'What engineering says is actually needed. Left blank it follows the order quantity.' },
    // Reference: RO in the quantity cells; the flag itself is asked in the dialog.
    { key: 'rateOnly', label: 'Rate only', type: 'text', width: 108, editable: true, options: [...YES_NO],
      dialogOnly: true, visibleWhen: ITEM_ONLY,
      hint: 'A priced line with no fixed quantity. Nothing raised against it can ever exceed it.' },
    { key: 'supplyRate', label: 'Supply rate', type: 'rate', width: 118, align: 'right', editable: true,
      visibleWhen: ITEM_ONLY, whenHeader: { ...SINGLE_RATE, hidden: true } },
    { key: 'rate', label: 'Installation rate', type: 'rate', width: 132, align: 'right', editable: true,
      visibleWhen: ITEM_ONLY, whenHeader: { ...SINGLE_RATE, label: 'Rate' } },
    // The reference shows the two halves and their sum side by side.
    { key: 'supplyAmount', label: 'Supply amount', type: 'money', width: 136, align: 'right',
      visibleWhen: ITEM_ONLY, whenHeader: { ...SINGLE_RATE, hidden: true },
      formula: { product: ['quantity', 'supplyRate'] } },
    { key: 'installationAmount', label: 'Installation amount', type: 'money', width: 150, align: 'right',
      visibleWhen: ITEM_ONLY, whenHeader: { ...SINGLE_RATE, hidden: true },
      formula: { product: ['quantity', 'rate'] } },
    { ...col.amount('Total amount'), visibleWhen: ITEM_ONLY },
    // Our working columns, after the reference's.
    { key: 'tendered', label: 'Tendered', type: 'text', width: 108, editable: true, options: [...YES_NO],
      visibleWhen: ITEM_ONLY,
      hint: 'No marks extra work outside the original tender. It is reconciled through Regularise BOQ.' },
    { key: 'billingTermLineId', label: 'Billing term', type: 'text', width: 190, editable: true,
      parentPicker: true, picksFrom: 'BILLING_TERM', visibleWhen: ITEM_ONLY },
    // docs/15 R-3.11 — per BOQ line, not per tenant. The company setting is the
    // default; a line with several billable estimates under it can need the
    // other method, and Billing Reconciliation reads the line's own answer.
    { key: 'progressMethod', label: 'Progress method', type: 'text', width: 170, editable: true,
      visibleWhen: ITEM_ONLY,
      options: [
        { value: 'CUMULATIVE', label: 'Cumulative — total done to date' },
        { value: 'MINIMUM', label: 'Minimum — the least complete of its estimates' },
      ],
      hint: 'Left empty it follows the company setting.' },
    // One of the Billing Summary Items, by its words (PROJ2-008).
    { key: 'summaryItem', label: 'Summary item', type: 'text', width: 170, editable: true,
      optionsFrom: { section: 'SUMMARY_ITEM', column: 'description' },
      hint: 'The billing summary line this rolls into on the customer’s own summary.' },
    /*
      Which half of quantity x rate the billing term's share comes off — the
      project's "Apply Billing Term to Quantity" (PROJ2-005), unless the line
      says otherwise here. Lines that disagreed with one another when the
      project answer became the single source kept their own answer in this
      cell (migration 225), so no bill already worked out changes.
    */
    { key: 'applyTermToQuantity', label: 'Term on quantity (this line)', type: 'text', width: 150, editable: true,
      options: [...YES_NO], dialogOnly: true, visibleWhen: ITEM_ONLY,
      hint: 'Left empty the line follows the project’s Apply Billing Term to Quantity.' },
    { ...col.hsn(), visibleWhen: ITEM_ONLY },
    { ...col.tax(), visibleWhen: ITEM_ONLY },
  ],
  // A running internal ref, continued into the common items.
  autoRef: { column: 'boqCode', style: 'number' },
  // One spreadsheet carries the BOQ and the estimate under it (docs/13), so
  // both grids offer the same import and it lands in both.
  sources: [{ key: 'excel', label: 'Import BOQ', source: 'excel' }],
}

/** Every cost type but a lump sum is a quantity at a rate. */
const PRICED_TYPES = ['MATERIAL', 'LABOUR', 'MATERIAL_AND_LABOUR', 'BUNDLED'] as const

/** docs/05 §1 §Estimation — internal cost, hung off a BOQ line. */
const costTypeColumn = {
  key: 'costType', label: 'Cost type', type: 'text', width: 170, editable: true, options: [...COST_TYPES],
  required: true,
  // Choosing Lumpsum makes the line one of itself: its rate is its amount.
  sets: { LUMPSUM: { quantity: '1' }, EXPENSE: { quantity: '1' } },
} as const

/** The reference's words for each cost type's fields (PROJ3-013), in the row dialog. */
const byCostType = (labels: Readonly<Record<string, string>>) => ({ column: 'costType', labels })

/** The item cell, narrowed to what the cost type allows, and named for it. */
const estimatedItem = (label: string) => ({
  ...col.item(label), lookupBy: ITEM_BY_COST_TYPE, required: true,
  labelsBy: byCostType({
    MATERIAL: 'Material', LABOUR: 'Labour', MATERIAL_AND_LABOUR: 'Material', BUNDLED: 'Item',
    LUMPSUM: 'Lumpsum', EXPENSE: 'Lumpsum',
  }),
})

/** The description, named for the cost type in the dialog. */
const estimatedDesc = {
  ...col.desc(),
  labelsBy: byCostType({
    MATERIAL: 'Material Description', LABOUR: 'Labour Description', MATERIAL_AND_LABOUR: 'Material Description',
    BUNDLED: 'Description', LUMPSUM: 'Lumpsum Description', EXPENSE: 'Lumpsum Description',
  }),
}

/**
 * Lumpsum lines take one figure; their quantity of 1 reads "-" (PROJ3-014).
 * Not required under a BOQ line, where "Per BOQ unit" may work it out.
 */
const estimatedQty = {
  ...col.qty('Estimated qty'),
  dashWhen: { column: 'costType', equals: [...LUMPSUM_TYPES] },
}

/** A lump sum has no unit either. */
const estimatedUnit = { ...col.unit(), dashWhen: { column: 'costType', equals: [...LUMPSUM_TYPES] } }

/** The rate, named for the cost type: a lump sum's rate is its amount. */
const estimatedRate = (label: string) => ({
  ...col.rate(label),
  labelsBy: byCostType({
    MATERIAL: 'Material Rate', LABOUR: 'Labour Rate', MATERIAL_AND_LABOUR: 'Labour Rate (M&L)',
    BUNDLED: 'Rate', LUMPSUM: 'Amount', EXPENSE: 'Amount',
  }),
})

/** The worked-out cost, read-only in the dialog and named for the cost type. */
const estimatedAmount = (label: string) => ({
  ...col.amount(label),
  labelsBy: byCostType({
    MATERIAL: 'Material Amount', LABOUR: 'Labour Amount', MATERIAL_AND_LABOUR: 'Amount', BUNDLED: 'Amount',
  }),
})

/** Material and Labour carries its material half in the supply column. */
const materialRate = {
  key: 'supplyRate', label: 'Material rate (M&L)', type: 'rate', width: 150, align: 'right', editable: true,
  visibleWhen: { column: 'costType', equals: ['MATERIAL_AND_LABOUR'] },
  hint: 'Only on a Material and Labour line. The estimated rate beside it is then the labour rate.',
} as const

/** Whether the customer is charged for it, asked on priced lines. */
const estimateBillable = { key: 'isBillable', label: 'Billable', type: 'text', width: 108, editable: true, options: [...YES_NO] } as const

/**
 * The estimate under one BOQ line — drawn inside "BOQ and Estimation",
 * beneath the BOQ line it costs (PROJ1-008). Its "+ Add" opens the same
 * dialog as the common items, asking the Cost Type first.
 */
const estimationGrid: LineGridDef = {
  section: 'ESTIMATION',
  parentSection: 'BOQ',
  nested: true,
  title: 'Estimation', n: '05',
  addLabel: 'Add estimation',
  addOpensDialog: true,
  detailTitle: 'Estimation',
  detailAsksFirst: 'costType',
  totalsAs: 'NONE',
  // The ones under a BOQ line. Those under none are the next grid.
  rows: { column: 'parentLineId', isSet: true },
  autoRef: { column: 'boqCode', style: 'code' },
  columns: [
    col.seq(),
    { key: 'boqCode', label: 'Estimation Ref No', type: 'text', width: 112, editable: true, required: true,
      hint: 'Given automatically; the Excel round trip matches estimates by it.' },
    costTypeColumn,
    estimatedItem('Estimated item'),
    estimatedDesc,
    { key: 'quantityMultiplier', label: 'Per BOQ unit', type: 'qty', width: 116, align: 'right', editable: true,
      visibleWhen: { column: 'costType', equals: [...PRICED_TYPES] },
      hint: 'How much of this goes into one unit of the BOQ line. Quantity is worked out from it.' },
    estimatedUnit,
    estimatedQty,
    materialRate,
    estimatedRate('Estimated rate'),
    estimateBillable,
    { key: 'allowedBrands', label: 'Allowed brands', type: 'text', width: 180, editable: true,
      hint: 'Comma separated. Buying outside this list raises an exception.' },
    // docs/24 §3.3 — when the site needs it. Left empty the schedule decides.
    { key: 'requiredBy', label: 'Needed on site by', type: 'date', width: 150, editable: true,
      hint: 'Left empty, the start of the schedule task for this BOQ line or its section, then the project’s planned start.' },
    // Moves the line under another BOQ line, or out to the common items.
    { key: 'parentLineId', label: 'Against BOQ line', type: 'text', width: 220, editable: true, parentPicker: true,
      dialogOnly: true,
      hint: 'Clear it and the line moves to Common Items and Overheads.' },
    estimatedAmount('Estimated cost'),
  ],
  sources: [{ key: 'excel', label: 'Import the estimate from Excel', source: 'excel' }],
}

/**
 * Estimation - Common Items and Overheads (reference).
 *
 * Estimate lines that belong to no one BOQ line — site room rent, transport,
 * testing and commissioning. Same storage as the estimate above (an
 * ESTIMATION line with no parent); its own grid so they are not lost among
 * three hundred lines that are. "+ Add line" opens the dialog of the same
 * name, Cost Type first, then the fields that type asks (PROJ3-013).
 */
const commonItemsGrid: LineGridDef = {
  section: 'ESTIMATION',
  key: 'COMMON_ITEMS',
  title: 'Estimation - Common Items and Overheads', n: '06',
  addLabel: 'Add line',
  addOpensDialog: true,
  detailTitle: 'Estimation - Common items and Overhead',
  detailAsksFirst: 'costType',
  totalsAs: 'NONE',
  rows: { column: 'parentLineId', isSet: false },
  // A hand-added line's ref is a short code (toi8hg), as on the estimate;
  // refs that came in from Excel keep what the sheet said (PROJ3-014).
  autoRef: { column: 'boqCode', style: 'code' },
  columns: [
    col.seq(),
    { key: 'boqCode', label: 'Ref', type: 'text', width: 92, editable: true, required: true },
    estimatedItem('Description'),
    estimatedDesc,
    costTypeColumn,
    estimatedUnit,
    { ...estimatedQty, label: 'Quantity', required: true },
    materialRate,
    estimatedRate('Rate'),
    estimateBillable,
    estimatedAmount('Est amount'),
  ],
  // The same spreadsheet as the BOQ: these are its `Overhead` rows.
  sources: [{ key: 'excel', label: 'Import the BOQ, estimate and overheads from Excel', source: 'excel' }],
}

/**
 * docs/05 §1 §Billing Details — how one BOQ line is billed in slices.
 *
 * Rows are the term lines themselves: a name, which half of the value they cut
 * from, their share of it, and whether the share is released by supply or by
 * installation. A BOQ line then points at the one it is billed under.
 */
const BOQ_LEVEL_ONLY = { field: 'billingMethod', equals: ['BOQ_LEVEL'] } as const

const billingTermGrid: LineGridDef = {
  section: 'BILLING_TERM',
  title: 'Billing terms',
  // Project Level billing has no terms (reference: the block disappears).
  visibleWhen: BOQ_LEVEL_ONLY,
  // One row per term, edited in a Billing Term dialog with its two sections.
  editor: 'billingTerms',
  addLabel: 'Add a billing term line',
  totalsAs: 'NONE',
  columns: [
    col.seq(),
    { key: 'termName', label: 'Term', type: 'text', width: 160, editable: true,
      hint: 'Lines sharing a name are one term. Their percentages must add to 100.' },
    { key: 'description', label: 'Billing line', type: 'text', editable: true },
    { key: 'termSection', label: 'Cuts from', type: 'text', width: 160, editable: true,
      options: [
        { value: 'SUPPLY', label: 'Supply value' },
        { value: 'INSTALLATION', label: 'Installation value' },
      ] },
    { key: 'percentage', label: 'Percentage', type: 'rate', width: 120, align: 'right', editable: true },
    { key: 'linkedTo', label: 'Released by', type: 'text', width: 160, editable: true,
      options: [
        { value: 'NONE', label: 'Nothing — billed on its own' },
        { value: 'SUPPLY', label: 'Supply' },
        { value: 'INSTALLATION', label: 'Installation' },
      ] },
  ],
  sources: [],
}

/**
 * docs/05 §1 §Billing Summary Items — how a bill is grouped for the customer.
 *
 * "Fire Hydrant System", "Fire Sprinkler Items": the headings a customer sees
 * on their own summary page, which BOQ lines roll into. Kept as its own grid
 * rather than as free text on the line, so the same heading spelt two ways
 * cannot become two headings.
 */
const summaryItemGrid: LineGridDef = {
  section: 'SUMMARY_ITEM',
  title: 'Billing Summary Items', n: '04',
  visibleWhen: BOQ_LEVEL_ONLY,
  addLabel: 'Add a summary item',
  totalsAs: 'NONE',
  columns: [
    col.seq(),
    { key: 'description', label: 'Summary item', type: 'text', editable: true,
      hint: 'The heading this group of BOQ lines is billed under.' },
  ],
  sources: [],
}

/** docs/05 §1 — the contract, and the BOQ everything downstream is attributed to. */
export const project = defineDocument({
  key: 'project',
  group: 'Project',
  blurb: 'Create projects with BOQ information. Add detailed cost estimation for all BOQ lines.',
  route: 'projects',
  label: 'Project',
  labelPlural: 'Projects',
  numbering: 'PROJECT',
  numberPrefix: 'PRO/',
  numberWidth: 3,
  numberOnSave: true,
  workflow: 'PROJECT',
  tabs: ['summary', 'related', 'revisions', 'form', 'drive'],
  createWizard: {
    title: 'Project setup',
    subtitle: 'These four settings are decided once and cannot be changed afterwards.',
    fields: [
      f.radio('pricingStyle', 'Pricing style', [['TWO_RATES', 'Two rates — supply and installation priced separately'], ['SINGLE_RATE', 'Single rate']]),
      f.radio('billingMethod', 'Billing method', [['BOQ_LEVEL', 'BOQ level'], ['PROJECT_LEVEL', 'Project level']]),
      f.radio('trackSiteInventory', 'Track inventory at the site?', [['YES', 'Yes'], ['NO', 'No']]),
      { ...taxUnit(true), width: 'full' as const },
    ],
  },
  form: {
    sections: [
      /*
        Reference: General Info, two columns — Customer | Job Type, Pricing
        Style | Project Name, Document Date | Link Type, Customer PO Number |
        Customer PO Date, Remarks across, Project Manager | External ID.
        Billing method is asked in Billing Details only (PROJ1-001); whether
        the site keeps stock is the wizard's answer, read on the Summary; the
        tax unit is the chip beside this title. Both stay declared by the
        wizard, so their stored answers are kept.
      */
      { key: 'general', n: '01', title: 'General Info', fields: [
        f.look('customerId', 'Customer', 'customer', true),
        jobType(),
        // Set once in the wizard and shown here, locked, so the promise the
        // wizard makes is one the form keeps.
        { ...f.choice('pricingStyle', 'Pricing style', [['TWO_RATES', 'Two rates'], ['SINGLE_RATE', 'Single rate']], false), lockAfterDraft: true },
        f.text('name', 'Project name', 'half', true),
        f.date('documentDate', 'Document date', true),
        // Reference: Link Type* — a top-level job is declared a Master Project;
        // anything else names the master it hangs off.
        { ...f.choice('linkType', 'Link type', [
          ['MASTER', 'Master project'], ['SUB_PROJECT', 'Sub-project'], ['PHASE', 'A phase'], ['VARIATION', 'A variation'],
        ], true), defaultValue: 'MASTER' },
        { ...f.look('masterProjectId', 'Master project', 'project', true),
          visibleWhen: { field: 'linkType', equals: ['SUB_PROJECT', 'PHASE', 'VARIATION'] } },
        // Still editable once approved, and saved by Update without a revision.
        { ...f.text('customerPoNo', 'Customer PO Number'), editableAfterApproval: true },
        { ...f.date('customerPoDate', 'Customer PO date'), editableAfterApproval: true },
        { ...f.notes(), editableAfterApproval: true },
        { ...f.look('projectManagerId', 'Project manager', 'employee'), editableAfterApproval: true },
        // docs/05 §1 — the customer's own reference for this job, which is what
        // their accounts department quotes back at you.
        f.text('externalId', 'External ID'),
        // HOME-008 — the stage Projects Health groups this project under.
        // Not on the reference's General Info, so it follows its fields with
        // our other extras; it moves with the job, so stays editable once approved.
        { ...f.look('stageId', 'Stage', 'project_stage'), editableAfterApproval: true },
        f.date('plannedStart', 'Planned start'),
        f.date('plannedFinish', 'Planned finish'),
        { key: 'documentNo', label: 'Project number', control: 'text', width: 'half',
          requiresSetting: 'manual_numbering_projects',
          hint: 'Left empty the next number in the series is used.' },
        taxUnitChip(),
      ] },
      // Reference: Billing and Site Info — straight after General Info.
      { key: 'site', n: '02', title: 'Billing and Site Info', fields: [
        scoped(f.look('billingContactId', 'Billing contact', 'contact'), ofParty('customerId')),
        scoped(f.look('billingAddressId', 'Billing address', 'address'), ofParty('customerId')),
        f.text('consignee', 'Consignee', 'half', true),
        // Reference: a dropdown of the customer's site addresses.
        scoped(f.look('siteAddressId', 'Site address', 'address'), ofPartyOrOurs('customerId'), 'none'),
        scoped(f.look('siteContactId', 'Site contact', 'contact'), OURS, 'none'),
        // Drives CGST + SGST or IGST on the order value and on the bills.
        { ...interstate(), hint: 'Yes when the site is in another state from the tax unit. The order value is then taxed as IGST.' },
        { key: 'creditPeriodDays', label: 'Credit period (in days)', control: 'number', width: 'half',
          hint: 'How long the customer has to pay a bill on this project.' },
      ] },
      /*
        docs/05 §1 §Billing Details — how the BOQ is billed, asked once for
        the whole project (reference). The billing-term grid follows straight
        on (`formOrder`), as the reference draws it inside this card.
      */
      { key: 'billing', n: '03', title: 'Billing Details', fields: [
        { ...f.choice('billingMethod', 'Billing method', [['BOQ_LEVEL', 'BOQ level'], ['PROJECT_LEVEL', 'Project level']], false), lockAfterDraft: true },
        // Read by billing: a billed rate other than the BOQ's is refused while
        // this is off (`billing_rate_differs_from_boq`).
        { ...f.yesNo('allowBaseRateAdjustment', 'Allow Base Rate Adjustment',
          'Lets a billed line be raised at a rate other than the one on the BOQ.'), visibleWhen: BOQ_LEVEL_ONLY },
        // The one answer every BOQ line bills by (PROJ2-005). A line may still
        // override it in its BOQ Detail, which is where lines that disagreed
        // before this was the single source keep their own answer.
        { ...f.yesNo('applyBillingTermToQuantity', 'Apply Billing Term to Quantity',
          'On: a term of 70% bills 70% of the quantity. Off: it bills the whole quantity at 70% of the rate.'), visibleWhen: BOQ_LEVEL_ONLY },
      ] },
    ],
  },
  /*
    Reference, top to bottom (PROJ2-001): General Info → Billing and Site Info
    → Project Team → Billing Details with its terms → Billing Summary Items →
    BOQ and Estimation (each BOQ line's estimate under it) → Common Items and
    Overheads → Non Tendered BOQ → Estimated Cost Summary → Project Value
    Summary.
  */
  formOrder: [
    'general', 'site', 'slot:projectTeam', 'billing', 'grid:BILLING_TERM', 'grid:SUMMARY_ITEM',
    'grid:BOQ', 'grid:COMMON_ITEMS', 'slot:ntBoq', 'projectFigures',
  ],
  // Reference Summary › Details, in its order and wording.
  summaryDetails: [
    { key: 'name', label: 'Project Name' },
    { key: 'trackSiteInventory', label: 'Want to track inventory at the site?' },
    { key: 'lines:BOQ', label: 'BOQ', noun: 'BOQ line' },
    { key: 'taxUnitId', label: 'Tax Unit' },
    { key: 'remarks', label: 'Remarks' },
    { key: 'customerPoNo', label: 'Customer PO Number' },
    { key: 'team', label: 'Team' },
    { key: 'interstate', label: 'Interstate (IGST)?' },
    { key: 'billingMethod', label: 'Billing Method' },
    { key: 'currency', label: 'Currency' },
  ],
  lines: boqGrid,
  grids: [billingTermGrid, estimationGrid, commonItemsGrid, summaryItemGrid],
  // Reference order: Customer, Document Number, Project Name, Document Date,
  // Job Type, Status — and Ongoing? (Yes/No) from the master row.
  columns: [
    { key: 'customerName', label: 'Customer', type: 'text' },
    { key: 'documentNo', label: 'Document number', type: 'docno', width: 150 },
    { key: 'name', label: 'Project name', type: 'text' },
    { key: 'documentDate', label: 'Document date', type: 'date', width: 140 },
    { key: 'jobTypeName', label: 'Job type', type: 'text', width: 200 },
    { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
    { key: 'ongoing', label: 'Ongoing?', type: 'text', width: 110 },
    { key: 'total', label: 'Project value', type: 'money', width: 156, align: 'right' },
    { key: 'closedOn', label: 'Closed on', type: 'date', width: 128, optional: true },
  ],
  // Reference Filters panel: Project, Customer, Job Type, Ongoing?, Tax Unit,
  // Status — then the number and date ranges the other lists share.
  filters: [
    { key: 'projectId', label: 'Project', control: 'lookup', lookup: { entity: 'project' } },
    { key: 'customerId', label: 'Customer', control: 'lookup', lookup: { entity: 'customer' } },
    { key: 'jobTypeId', label: 'Job type', control: 'lookup', lookup: { entity: 'job_type' } },
    { key: 'ongoing', label: 'Ongoing?', control: 'select',
      options: [{ value: 'YES', label: 'Yes' }, { value: 'NO', label: 'No' }] },
    { key: 'taxUnitId', label: 'Tax unit', control: 'lookup', lookup: { entity: 'tax_unit' } },
    { key: 'documentStatus', label: 'Status', control: 'status' },
    { key: 'documentNo', label: 'Document number', control: 'text' },
    { key: 'documentDate', label: 'Document date', control: 'dateRange' },
  ],
  // Change Currency / Change Tax Unit restate an approved project without
  // reopening it, as they do on the synced documents.
  actions: [...FULL_ACTIONS, 'changeCurrency', 'changeTaxUnit'],
  rules: ['billing_term_percentages', 'summary_item_listed'],
  createsNext: [
    { docType: 'material_request', label: 'Material request', whenStatus: 'APPROVED' },
    { docType: 'purchase_order', label: 'Purchase order', whenStatus: 'APPROVED' },
    { docType: 'billing_request', label: 'Billing request', whenStatus: 'APPROVED' },
  ],
} as const)

/** docs/05 §3 — what the site asks for, drawn from the estimate. */
export const materialRequest = defineDocument({
  key: 'material_request',
  group: 'Project',
  blurb: 'Request materials required for the project. Pick the material from the detailed estimation.',
  route: 'mat_reqs',
  label: 'Material request',
  labelPlural: 'Material requests',
  numbering: 'MATERIAL_REQUEST',
  numberPrefix: 'MR/',
  workflow: 'MATERIAL_REQUEST',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Create a material request',
    subtitle: 'Select the project.',
    fields: [f.look('projectId', 'Project', 'project', true, 'full')],
  },
  form: {
    sections: [
      /*
        Reference General Info: Project* (locked), Customer* (the project's,
        locked), Document Date*, Required Date*, Remarks — tax unit as the
        chip. The customer is the project's on every save (PROJ3-018), so a
        request can never name another customer than its job.
      */
      { key: 'general', n: '01', title: 'General Info', fields: [
        { ...f.look('projectId', 'Project', 'project', true), lockAfterDraft: true },
        { ...f.look('customerId', 'Customer', 'customer', true), readOnlyWhenSet: ['projectId'],
          hint: 'The project’s customer.' },
        f.date('documentDate', 'Document date', true),
        f.date('requiredDate', 'Required date', true),
        f.notes(),
        f.look('requestedById', 'Requested by', 'employee'),
        jobType(),
        // Reference MR Details: Tax Unit.
        taxUnitChip(),
      ] },
    ],
  },
  /*
    Reference "Materials Required": #, Item, Allowed Brands, Quantity (with
    its unit), Type, BOQ Ref, For. "+ Add items" opens Material Info — Item*,
    Unit*, Quantity*, Description, For BOQ / Project?*, Type* (PROJ3-019).
  */
  lines: {
    title: 'Materials required', n: '02',
    addLabel: 'Add items',
    addOpensDialog: true,
    detailTitle: 'Material Info',
    rowDefaults: { forScope: 'BOQ', isBillable: 'YES' },
    columns: [
      col.seq(), { ...col.item('Item'), required: true }, col.desc(),
      { key: 'allowedBrands', label: 'Allowed brands', type: 'text', width: 160 },
      { ...col.qty('Quantity'), required: true },
      // Said after the quantity, in its cell: "1 Nos".
      { ...col.unit(), secondary: true, required: true },
      /*
        What the stores team decided to actually buy.

        "Stores team can decide the purchase quantity (out of requested) based
        on the stock already available in the warehouse." The request says what
        the site wants; this says what procurement should go and get, and the
        two are different every time there is stock on the shelf. Left empty it
        is the requested quantity (docs/video-notes/06 §9).
      */
      { key: 'plannedQty', label: 'To buy', type: 'qty', width: 110, align: 'right', editable: true,
        hint: 'Left empty, the whole requested quantity is bought.' },
      { key: 'isBillable', label: 'Type', type: 'text', width: 130, editable: true, required: true, control: 'radio',
        options: [{ value: 'NO', label: 'Non Billable' }, { value: 'YES', label: 'Billable' }] },
      { ...col.boqRef(), editable: true, editableWhen: { column: 'forScope', equals: ['BOQ'] },
        hint: 'The BOQ ref no this material is for.' },
      /*
        Reference: "For BOQ / Project?*" — what a hand-added material is for.
        A line picked from the estimate is for its BOQ line already; one typed
        by hand says whether it serves a BOQ line (named by its ref) or the
        project in general. Asked in Material Info; the grid's For cell says
        what it came to.
      */
      { key: 'forScope', label: 'For BOQ / Project?', type: 'text', width: 150, editable: true, required: true,
        control: 'radio', dialogOnly: true,
        options: [{ value: 'BOQ', label: 'BOQ Line' }, { value: 'PROJECT', label: 'Project' }],
        lockedWhen: { column: 'fromLineId', isSet: true } },
      { key: 'forProjectId', label: 'Buying for project', type: 'text', width: 180, editable: true, lookup: { entity: 'project' },
        dialogOnly: true,
        hint: 'Left empty it is for this project. Set it when you are buying on behalf of another job.' },
      // Not estimated (red) for a material nowhere on the estimate, else the
      // project it is bought for, else Self (PROJ3-020; mr-presenter.ts).
      { key: 'forStatus', label: 'For', type: 'text', width: 170, badges: { 'Not estimated': 'bad' } },
    ],
    sources: [
      { key: 'estimation', label: 'Select items', source: 'estimation' },
      { key: 'excel', label: 'Import from Excel', source: 'excel' },
    ],
  },
  // Reference list: Approved By, Approved On, Closed Status, Document Date,
  // Document Number, Project, Document Status, Remarks. Approved by/on and
  // Remarks come from the chassis list catalogue; Closed status is this list's.
  columns: [
    ...baseColumns(null, 'Requested value'),
    { key: 'closedStatus', label: 'Closed status', type: 'text', width: 140 },
    { key: 'fulfilmentStatus', label: 'Purchase status', type: 'text', width: 170, optional: true },
    { key: 'requiredDate', label: 'Required date', type: 'date', width: 140, optional: true },
    { key: 'customerName', label: 'Customer', type: 'text', width: 200, optional: true },
    // Set up Purchase Plan (PROJ3-016): whether it was set, and when and by whom.
    { key: 'purchasePlanEnabled', label: 'Purchase plan enabled?', type: 'text', width: 170, optional: true },
    { key: 'purchasePlan', label: 'Purchase plan', type: 'text', width: 200, optional: true },
  ],
  filters: [
    ...baseFilters(null),
    { key: 'customerId', label: 'Customer', control: 'lookup', lookup: { entity: 'customer' } },
    { key: 'purchasePlan', label: 'Purchase plan', control: 'select', options: [
      { value: 'SET', label: 'Set up' },
      { value: 'NOT_SET', label: 'Not set up yet' },
    ] },
    { key: 'submittedById', label: 'Submitted by', control: 'lookup', lookup: { entity: 'employee' } },
    // Its own range, beside the document date's (MR-003).
    { key: 'requiredDate', label: 'Required date', control: 'dateRange' },
    { key: 'fulfilment', label: 'Purchase status', control: 'select', options: [
      { value: 'OPEN', label: 'Anything still open' },
      { value: 'PARTIAL', label: 'Partly purchased' },
      { value: 'COMPLETE', label: 'Fully purchased' },
    ] },
  ],
  // Copy: from the create dialog's "copy another request" (MR-004).
  actions: [...BASIC_ACTIONS, 'clone', 'preClose', 'undoPreClose', 'changeTaxUnit'],
  rules: ['excess_purchase_quantity'],
  createsNext: [
    // docs/24 §3.4 — the purchase executive's step between the site and the order.
    { docType: 'purchase_requisition', label: 'Purchase requisition', whenStatus: 'APPROVED' },
    { docType: 'purchase_order', label: 'Purchase order', whenStatus: 'APPROVED' },
    { docType: 'material_transfer', label: 'Material transfer', whenStatus: 'APPROVED' },
  ],
} as const)
